import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScanLine, Plus, Loader2, Star, ChefHat, AlertTriangle, Edit3, Save, X } from 'lucide-react';
import { useLanguage, api } from '../../context/AppContext';
import { toast } from 'sonner';

const MEAL_TYPES = [
  { value: 'dejeuner', label: '🌅', key: 'breakfast', titleFr: 'Déjeuner' },
  { value: 'diner', label: '☀️', key: 'lunch', titleFr: 'Dîner' },
  { value: 'souper', label: '🌙', key: 'dinner', titleFr: 'Souper' },
  { value: 'collation', label: '🍎', key: 'snack', titleFr: 'Collation' },
  { value: 'repas', label: '🍽️', key: 'meal', titleFr: 'Repas' },
];

const BarcodeResultModal = ({
  open,
  onOpenChange,
  lookingUp,
  barcodeResult,
  barcodeServings,
  onServingsChange,
  barcodeMealType,
  onMealTypeChange,
  onAdd,
  onCancel,
  adding,
  onNutritionOverride,
}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [togglingFav, setTogglingFav] = useState(false);
  const [isFav, setIsFav] = useState(barcodeResult?.is_favorite ?? false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedNutrition, setEditedNutrition] = useState({});

  // Update local state when barcodeResult changes
  React.useEffect(() => {
    setIsFav(barcodeResult?.is_favorite ?? false);
    setIsEditing(false);
    if (barcodeResult?.nutrition_per_100g) {
      setEditedNutrition({ ...barcodeResult.nutrition_per_100g });
    }
  }, [barcodeResult]);

  const n = isEditing ? editedNutrition : (barcodeResult?.nutrition_per_100g ?? {});

  const startEditing = () => {
    setEditedNutrition({ ...(barcodeResult?.nutrition_per_100g || {}) });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditedNutrition({ ...(barcodeResult?.nutrition_per_100g || {}) });
  };

  const saveOverride = async () => {
    if (!barcodeResult) return;
    try {
      // Save to user_custom_foods via API
      await api.post('/scan/custom-override', {
        barcode: barcodeResult.barcode,
        product_name: barcodeResult.product_name,
        brand: barcodeResult.brand,
        image_url: barcodeResult.image_url,
        serving_size: barcodeResult.serving_size,
        nutriscore: barcodeResult.nutriscore,
        nova_group: barcodeResult.nova_group,
        categories: barcodeResult.categories,
        ingredients: barcodeResult.ingredients,
        nutrition_per_100g: editedNutrition,
      });
      // Update the local barcodeResult nutrition
      if (onNutritionOverride) {
        onNutritionOverride(editedNutrition);
      }
      setIsEditing(false);
      toast.success(t('barcode.toast_override_saved', 'Correction sauvegardee! Ce produit utilisera tes valeurs au prochain scan.'));
    } catch {
      toast.error(t('barcode.toast_save_error', 'Erreur de sauvegarde'));
    }
  };

  const handleEditField = (field, value) => {
    setEditedNutrition(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  const handleToggleFavorite = async () => {
    if (!barcodeResult) return;
    setTogglingFav(true);
    try {
      if (isFav) {
        await api.delete(`/scan/favorites/${barcodeResult.barcode}`);
        setIsFav(false);
        toast.success(t('barcode.toast_fav_removed', 'Retiré des favoris'));
      } else {
        await api.post('/scan/favorites', {
          barcode: barcodeResult.barcode,
          product_name: barcodeResult.product_name,
          brand: barcodeResult.brand,
          image_url: barcodeResult.image_url,
          nutrition_per_100g: barcodeResult.nutrition_per_100g,
        });
        setIsFav(true);
        toast.success(t('barcode.toast_fav_added', 'Ajouté aux favoris!'));
      }
    } catch {
      toast.error('Erreur');
    } finally {
      setTogglingFav(false);
    }
  };

  const handleTransformToRecipe = () => {
    if (!barcodeResult) return;
    const params = new URLSearchParams({
      autofill: '1',
      name: barcodeResult.product_name + (barcodeResult.brand ? ` (${barcodeResult.brand})` : ''),
      calories: String(n.calories ?? 0),
      protein_g: String(n.protein ?? 0),
      carbs_g: String(n.carbs ?? 0),
      fats_g: String(n.fat ?? 0),
      fiber_g: String(n.fiber ?? 0),
      barcode: barcodeResult.barcode,
    });
    onOpenChange(false);
    navigate(`/my-recipes?${params.toString()}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-green-500" />
            {t('barcode.scanned_product', 'Produit scanné')}
          </DialogTitle>
        </DialogHeader>
        
        {lookingUp ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
            <p className="text-muted-foreground">
              {t('barcode.looking_up', 'Recherche du produit...')}
            </p>
          </div>
        ) : barcodeResult && (
          <div className="space-y-4 py-4">
            {/* Frequency Alert */}
            {barcodeResult.frequency_alert && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-start gap-2" data-testid="frequency-alert">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-300">
                    {t('barcode.freq_alert', 'Tu scannes souvent ce produit ({{count}}x cette semaine)!', { count: barcodeResult.scan_count_7d })}
                  </p>
                  <p className="text-xs text-amber-400/70 mt-0.5">
                    {t('barcode.freq_alert_tip', 'Ajoute-le aux favoris ou crée une recette rapide!')}
                  </p>
                </div>
              </div>
            )}

            {/* Product Image & Name + Star */}
            <div className="flex gap-4 items-start">
              {barcodeResult.image_url && (
                <img 
                  src={barcodeResult.image_url} 
                  alt={barcodeResult.product_name}
                  className="w-20 h-20 object-cover rounded-lg border border-border"
                />
              )}
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <h3 className="font-heading text-lg text-foreground pr-2">
                    {barcodeResult.product_name}
                  </h3>
                  <button
                    onClick={handleToggleFavorite}
                    disabled={togglingFav}
                    className={`flex-shrink-0 p-1.5 rounded-lg transition-all ${
                      isFav
                        ? 'bg-amber-500/20 text-amber-400'
                        : 'bg-secondary/50 text-muted-foreground hover:text-amber-400 hover:bg-amber-500/10'
                    }`}
                    data-testid="favorite-star-btn"
                  >
                    {togglingFav ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Star className={`w-5 h-5 ${isFav ? 'fill-amber-400' : ''}`} />
                    )}
                  </button>
                </div>
                {barcodeResult.brand && (
                  <p className="text-sm text-muted-foreground">{barcodeResult.brand}</p>
                )}
                {barcodeResult.nutriscore && (
                  <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-bold text-white ${
                    barcodeResult.nutriscore === 'A' ? 'bg-green-500' :
                    barcodeResult.nutriscore === 'B' ? 'bg-lime-500' :
                    barcodeResult.nutriscore === 'C' ? 'bg-yellow-500' :
                    barcodeResult.nutriscore === 'D' ? 'bg-orange-500' :
                    'bg-red-500'
                  }`}>
                    Nutri-Score {barcodeResult.nutriscore}
                  </span>
                )}
                {isFav && (
                  <span className="inline-block mt-1 ml-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-400 uppercase">
                    Favori
                  </span>
                )}
              </div>
            </div>

            {/* Nutrition per 100g — EDITABLE */}
            <div className="bg-secondary/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-muted-foreground uppercase font-heading">
                  {t('barcode.values_per_100g', 'Valeurs pour 100g')}
                  {barcodeResult.is_custom_override && (
                    <span className="ml-2 text-primary">(corrige)</span>
                  )}
                </p>
                {!isEditing ? (
                  <button
                    onClick={startEditing}
                    className="flex items-center gap-1 text-[11px] text-primary hover:text-primary/80 transition-colors"
                    data-testid="edit-nutrition-override-btn"
                  >
                    <Edit3 className="w-3 h-3" />
                    {t('barcode.correct', 'Corriger')}
                  </button>
                ) : (
                  <div className="flex gap-1.5">
                    <button
                      onClick={cancelEditing}
                      className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <button
                      onClick={saveOverride}
                      className="flex items-center gap-1 text-[11px] text-primary font-bold hover:text-primary/80"
                      data-testid="save-nutrition-override-btn"
                    >
                      <Save className="w-3 h-3" />
                      {t('common.save', 'Sauvegarder')}
                    </button>
                  </div>
                )}
              </div>
              {isEditing ? (
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'calories', label: 'Calories', unit: 'kcal' },
                    { key: 'protein', label: t('meal.protein', 'Protéines'), unit: 'g' },
                    { key: 'carbs', label: t('meal.carbs', 'Glucides'), unit: 'g' },
                    { key: 'fat', label: t('meal.fats', 'Lipides'), unit: 'g' },
                    { key: 'fiber', label: t('meal.fiber', 'Fibres'), unit: 'g' },
                  ].map(({ key, label, unit }) => (
                    <div key={key} className="space-y-0.5">
                      <label className="text-[10px] text-muted-foreground">{label} ({unit})</label>
                      <Input
                        type="number"
                        step="0.1"
                        value={editedNutrition[key] ?? 0}
                        onChange={(e) => handleEditField(key, e.target.value)}
                        className="h-8 bg-background border-primary/30 text-sm"
                        data-testid={`edit-override-${key}`}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground text-sm">Calories</span>
                    <span className="font-medium text-foreground">{n.calories ?? 0} kcal</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground text-sm">{t('meal.protein', 'Protéines')}</span>
                    <span className="font-medium text-chart-protein">{n.protein ?? 0}g</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground text-sm">{t('meal.carbs', 'Glucides')}</span>
                    <span className="font-medium text-chart-carbs">{n.carbs ?? 0}g</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground text-sm">{t('meal.fats', 'Lipides')}</span>
                    <span className="font-medium text-chart-fat">{n.fat ?? 0}g</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground text-sm">{t('meal.fiber', 'Fibres')}</span>
                    <span className="font-medium text-foreground">{n.fiber ?? 0}g</span>
                  </div>
                </div>
              )}
            </div>

            {/* Servings selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                {t('barcode.servings_consumed', 'Portions consommées (100g chacune)')}
              </label>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={() => onServingsChange(Math.max(0.5, barcodeServings - 0.5))} className="w-10 h-10">-</Button>
                <Input type="number" min="0.5" step="0.5" value={barcodeServings} onChange={(e) => onServingsChange(parseFloat(e.target.value) || 1)} className="w-20 text-center bg-secondary border-border" />
                <Button variant="outline" size="sm" onClick={() => onServingsChange(barcodeServings + 0.5)} className="w-10 h-10">+</Button>
              </div>
            </div>

            {/* Meal Type Selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                {t('barcode.meal_type', 'Type de repas')}
              </label>
              <div className="grid grid-cols-5 gap-1">
                {MEAL_TYPES.map(type => (
                  <button
                    key={type.value}
                    onClick={() => onMealTypeChange(type.value)}
                    className={`flex flex-col items-center p-2 rounded-lg border transition-all ${
                      barcodeMealType === type.value 
                        ? 'bg-primary/20 border-primary' 
                        : 'bg-secondary border-border hover:border-primary/50'
                    }`}
                  >
                    <span className="text-lg">{type.label}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {t(`barcode.meal_${type.key}`, type.titleFr)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Total nutrition preview */}
            <div className="bg-primary/10 rounded-lg p-4 border border-primary/30">
              <p className="text-xs text-primary mb-2 uppercase font-heading">
                {t('barcode.total_for_servings', 'Total pour {{count}} portion(s)', { count: barcodeServings })}
              </p>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div>
                  <p className="text-lg font-bold text-foreground">{Math.round((n.calories ?? 0) * barcodeServings)}</p>
                  <p className="text-xs text-muted-foreground">kcal</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-chart-protein">{Math.round((n.protein ?? 0) * barcodeServings)}g</p>
                  <p className="text-xs text-muted-foreground">Prot.</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-chart-carbs">{Math.round((n.carbs ?? 0) * barcodeServings)}g</p>
                  <p className="text-xs text-muted-foreground">Gluc.</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-chart-fat">{Math.round((n.fat ?? 0) * barcodeServings)}g</p>
                  <p className="text-xs text-muted-foreground">Lip.</p>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="space-y-2 pt-2">
              {/* Primary: Add as meal */}
              <Button
                className="w-full bg-green-500 hover:bg-green-600 text-white"
                onClick={onAdd}
                disabled={adding}
                data-testid="add-barcode-meal-btn"
              >
                {adding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                {t('barcode.add_as_meal', 'Ajouter comme repas')}
              </Button>

              {/* Secondary: Transform to recipe */}
              <Button
                variant="outline"
                className="w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                onClick={handleTransformToRecipe}
                data-testid="transform-to-recipe-btn"
              >
                <ChefHat className="w-4 h-4 mr-2" />
                {t('barcode.transform_to_recipe', 'Transformer en recette perso')}
              </Button>

              <Button variant="outline" className="w-full" onClick={onCancel}>
                {t('common.cancel', 'Annuler')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BarcodeResultModal;
